/**
 * Main App Component with Routing
 *
 * Handles routing for different verification flows:
 * - /:locale/face-liveness → FaceVerificationPage (with locale)
 * - /face-liveness → FaceVerificationPage (backward compatibility)
 * - /:locale/legal-id-verification → LegalIdVerificationPage (with locale)
 * - /legal-id-verification → LegalIdVerificationPage (backward compatibility)
 * - /:locale/ssn-card-verification → SsnCardVerificationPage (with locale)
 * - /ssn-card-verification → SsnCardVerificationPage (backward compatibility)
 * - /:locale/address-history → AddressHistoryPage (with locale)
 * - /address-history → AddressHistoryPage (backward compatibility)
 * - /:locale/workflow-face-liveness → WorkflowFaceVerificationPage (with locale)
 * - /workflow-face-liveness → WorkflowFaceVerificationPage (backward compatibility)
 * - /:locale/workflow-legal-id → WorkflowLegalIdPage (with locale)
 * - /workflow-legal-id → WorkflowLegalIdPage (backward compatibility)
 * - /testing → TestingPage (simulates customer integration)
 *
 * The *-mock routes and /testing render outside production only — see NonProductionOnly.
 */

import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { FaceVerificationPage, FaceVerificationMockPage, LegalIdVerificationPage, LegalIdVerificationMockPage, RegulaLegalIdVerificationPage, SsnCardVerificationPage, SsnCardVerificationMockPage, TestingPage, HomePage, NotFoundPage, WorkflowLegalIdPage, WorkflowFaceVerificationPage, WorkflowPhonePage, WorkflowSsnPage, AddressHistoryPage, AddressHistoryMockPage, ConsentPage, ConfirmInfoPage, CollectAddressPage } from "./routes";
import { LocaleProvider } from "./components/LocaleProvider";
import { ConsumerProvider } from "./components/ConsumerProvider";
import { BrandLayout } from "./components/brandLogo/BrandLayout";
import { areMockRoutesEnabled } from "./shared/utils/mockRoutes";
import "./App.css";

// Layout component that provides LocaleProvider inside Routes
function LocaleLayout() {
  return (
    <LocaleProvider>
      <Outlet />
    </LocaleProvider>
  );
}

// Gate for the mock and testing routes. They emit a "passed" result over the real
// postMessage protocol, so on a production host they would hand any embedding page a
// success message carrying a trusted IDMe origin. Everywhere else they render NotFound.
function NonProductionOnly() {
  return areMockRoutesEnabled() ? <Outlet /> : <NotFoundPage />;
}

function App() {
  return (
    <BrowserRouter>
      {/* Consumer branding (from IDME_INIT; query params on non-production hosts) for every route */}
      <ConsumerProvider>
        <Routes>
          {/* Verification screens. BrandLayout renders their shared header when the
              Fragno logo flag (vex_7983_fragno_logo_enabled) is on. */}
          <Route element={<BrandLayout />}>
            {/* Face verification routes */}
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/face-liveness" element={<FaceVerificationPage />} />
            </Route>

            {/* Legal ID routes */}
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/legal-id-verification" element={<LegalIdVerificationPage />} />
              <Route path="/:locale/regula-legal-id-verification" element={<RegulaLegalIdVerificationPage />} />
            </Route>

            {/* SSN card verification routes */}
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/ssn-card-verification" element={<SsnCardVerificationPage />} />
            </Route>

            {/* Address verification routes */}
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/address-history" element={<AddressHistoryPage />} />
            </Route>

            {/* Workflow engine routes — separate from IVA flows */}
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/workflow-face-liveness" element={<WorkflowFaceVerificationPage />} />
              <Route path="/:locale/workflow-legal-id" element={<WorkflowLegalIdPage />} />
              <Route path="/:locale/workflow-consent" element={<ConsentPage />} />
            </Route>

            {/* Workflow phone + SSN + confirm-info + collect-address */}
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/workflow-phone" element={<WorkflowPhonePage />} />
              <Route path="/:locale/workflow-ssn" element={<WorkflowSsnPage />} />
              <Route path="/:locale/workflow-confirm-info" element={<ConfirmInfoPage />} />
              <Route path="/:locale/workflow-collect-address" element={<CollectAddressPage />} />
            </Route>

            {/* Backward compatibility - routes without locale */}
            <Route path="/face-liveness" element={<FaceVerificationPage />} />
            <Route path="/legal-id-verification" element={<LegalIdVerificationPage />} />
            <Route path="/regula-legal-id-verification" element={<RegulaLegalIdVerificationPage />} />
            <Route path="/ssn-card-verification" element={<SsnCardVerificationPage />} />
            <Route path="/address-history" element={<AddressHistoryPage />} />

            {/* Workflow engine routes — separate from IVA flows */}
            <Route path="/workflow-legal-id" element={<WorkflowLegalIdPage />} />
            <Route path="/workflow-face-liveness" element={<WorkflowFaceVerificationPage />} />
            <Route path="/workflow-phone" element={<WorkflowPhonePage />} />
            <Route path="/workflow-ssn" element={<WorkflowSsnPage />} />
            <Route path="/workflow-consent" element={<ConsentPage />} />
            <Route path="/workflow-confirm-info" element={<ConfirmInfoPage />} />
            <Route path="/workflow-collect-address" element={<CollectAddressPage />} />
          </Route>

          {/* Mock and testing routes — non-production environments only, see NonProductionOnly */}
          <Route element={<NonProductionOnly />}>
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/face-liveness-mock" element={<FaceVerificationMockPage />} />
              <Route path="/:locale/address-history-mock" element={<AddressHistoryMockPage />} />
            </Route>
            <Route element={<LocaleLayout />}>
              <Route path="/:locale/legal-id-verification-mock" element={<LegalIdVerificationMockPage />} />
              <Route path="/:locale/ssn-card-verification-mock" element={<SsnCardVerificationMockPage />} />
            </Route>

            {/* Backward compatibility - routes without locale */}
            <Route path="/face-liveness-mock" element={<FaceVerificationMockPage />} />
            <Route path="/legal-id-verification-mock" element={<LegalIdVerificationMockPage />} />
            <Route path="/ssn-card-verification-mock" element={<SsnCardVerificationMockPage />} />
            <Route path="/address-history-mock" element={<AddressHistoryMockPage />} />

            <Route path="/testing" element={<TestingPage />} />
          </Route>

          {/* Other routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ConsumerProvider>
    </BrowserRouter>
  );
}

export default App;
